import { StandardEvents } from '@shopify/events';

const EVENT_NAMES = {
  addToCart: 'btk_funnel_add_to_cart',
  drawerOpen: 'btk_funnel_cart_drawer_open',
  checkoutClick: 'btk_funnel_checkout_click',
};

const processedCartEvents = new WeakSet();
const processedClicks = new WeakSet();
const debugEvents = (window.__btkFunnelEvents = window.__btkFunnelEvents || []);
let pendingDrawerSource = null;
let pendingDrawerSourceExpiresAt = 0;
let cartDrawerOpen = document.querySelector('#cart-drawer')?.hasAttribute('open') || false;

function deviceType() {
  return window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
}

function eventId() {
  try {
    return window.crypto.randomUUID();
  } catch (error) {
    return `btk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function emit(name, parameters = {}) {
  const payload = {
    event_id: eventId(),
    device_type: deviceType(),
    page_path: window.location.pathname,
    ...parameters,
  };

  debugEvents.push({ name, parameters: payload, emitted_at: new Date().toISOString() });

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, { ...payload, transport_type: 'beacon' });
    }
  } catch (error) {
    // Analytics must never interrupt the storefront.
  }
}

function numericId(value) {
  return String(value || '').split('/').pop().split('?')[0];
}

function currencyCode(item) {
  return item?.currency || window.Shopify?.currency?.active || 'USD';
}

function trackSuccessfulAdd(event) {
  if (event.action !== 'add' || processedCartEvents.has(event)) return;
  processedCartEvents.add(event);
  pendingDrawerSource = 'automatic_add';
  pendingDrawerSourceExpiresAt = Date.now() + 4000;

  event.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      const cartItems = Array.isArray(detail?.items) ? detail.items : [];
      const items = (event.lines || []).map((line) => {
        const variantId = numericId(line.merchandiseId);
        const cartItem = cartItems.find((item) => String(item.variant_id) === variantId);
        const quantity = Number(line.quantity) || 1;
        const price = Number(cartItem?.final_price ?? cartItem?.price ?? 0) / 100;
        return {
          item_id: cartItem?.sku || variantId,
          item_name: cartItem?.product_title || '',
          item_variant: cartItem?.variant_title || '',
          product_id: String(cartItem?.product_id || detail?.productId || ''),
          variant_id: variantId,
          price,
          quantity,
        };
      });
      const firstItem = items[0] || {};
      emit(EVENT_NAMES.addToCart, {
        product_id: firstItem.product_id || String(detail?.productId || ''),
        product_title: firstItem.item_name || '',
        variant_id: firstItem.variant_id || '',
        variant_title: firstItem.item_variant || '',
        currency: currencyCode(cartItems[0]),
        value: items.reduce((sum, item) => sum + item.price * item.quantity, 0),
        quantity: items.reduce((sum, item) => sum + item.quantity, 0),
        items,
      });
    })
    .catch(() => {});
}

function markManualDrawerOpen(event) {
  const triggeredByCartIcon = eventPath(event).some(
    (node) => node instanceof Element && node.matches('[data-testid="cart-drawer-trigger"]')
  );
  if (!triggeredByCartIcon) return;
  pendingDrawerSource = 'manual_icon';
  pendingDrawerSourceExpiresAt = Date.now() + 2000;
}

function trackDrawerOpen(event) {
  const drawer = event.target instanceof Element ? event.target.closest('#cart-drawer') : null;
  if (!drawer || cartDrawerOpen) return;
  cartDrawerOpen = true;
  const openSource = Date.now() <= pendingDrawerSourceExpiresAt ? pendingDrawerSource : 'restored_or_other';
  pendingDrawerSource = null;
  pendingDrawerSourceExpiresAt = 0;
  emit(EVENT_NAMES.drawerOpen, { open_source: openSource });
}

function trackDrawerClose(event) {
  const drawer = event.target instanceof Element ? event.target.closest('#cart-drawer') : null;
  if (drawer) cartDrawerOpen = false;
}

function eventPath(event) {
  return typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
}

function isCheckoutClick(event) {
  return eventPath(event).some(
    (node) => node instanceof Element && node.matches('#cart-drawer [name="checkout"], #cart-drawer .cart__checkout-button')
  );
}

function trackClick(event) {
  if (processedClicks.has(event) || !isCheckoutClick(event)) return;
  processedClicks.add(event);
  emit(EVENT_NAMES.checkoutClick, { placement: 'cart_drawer' });
}

document.addEventListener(StandardEvents.cartLinesUpdate, trackSuccessfulAdd);
document.addEventListener('pointerdown', markManualDrawerOpen, true);
document.addEventListener('click', markManualDrawerOpen, true);
document.addEventListener('theme-drawer:open', trackDrawerOpen);
document.addEventListener('theme-drawer:close', trackDrawerClose);
document.addEventListener('click', trackClick, true);
